import { isValidElement, useMemo, useState, type ReactNode } from 'react';
import { locale, t } from '../i18n/index.ts';
import { Icon } from './Icon.tsx';
import { usePref } from './nav.tsx';
import { Skeleton } from './ui.tsx';
import { compareKeys, nodeText, sortLocale, sortValue, type SortKey } from './table-sort.ts';
import { TableViewPanel, type Density, type Sort, type TableView } from './TableView.tsx';

/**
 * The one table of the app. Every list of figures (reports, registers, statements, keys…) goes
 * through it, so they all read the same way: a calm header, figures on the right with tabular
 * digits, a whole row that opens its record (also from the keyboard), a totals row, and columns
 * that sort when their header is pressed. Sorting is a view only: it never changes the data, and
 * pressing the header a third time returns to the order the server gave.
 */

/** True when a cell already holds something to press (a link, button, checkbox…): the row button then goes elsewhere. */
function interactive(node: ReactNode): boolean {
  if (Array.isArray(node)) return node.some(interactive);
  if (
    !isValidElement<{
      children?: ReactNode;
      href?: string;
      onClick?: unknown;
      to?: unknown;
    }>(node)
  )
    return false;
  const type = node.type;
  if (
    typeof type === 'string' ? ['a', 'button', 'input', 'select', 'textarea'].includes(type) : node.props.to !== undefined || node.props.onClick !== undefined
  )
    return true;
  return interactive(node.props.children);
}

export interface DataRow {
  key: string;
  cells: ReactNode[];
  onClick?: () => void;
  /** Own sort values, one per column, for cells whose text does not sort well (dates, codes). */
  sort?: SortKey[];
  /** Highlights the row, e.g. the one that is open beside the table. */
  selected?: boolean;
}

export function DataTable({
  head,
  rows,
  foot,
  empty,
  phoneHide = [],
  left = [],
  sortable = true,
  caption,
  loading,
  dense,
  view,
}: {
  head: string[];
  rows: DataRow[];
  foot?: ReactNode[] | null;
  empty?: string;
  /** Columns left out on a phone, so the table fits without scrolling sideways. */
  phoneHide?: number[];
  /** Text columns after the first, read from the left like the first one. */
  left?: number[];
  /** Headers sort their column when pressed (on by default; off for tables whose order is the meaning, e.g. a ledger). */
  sortable?: boolean;
  /** A name for screen readers when the heading above does not say what the table holds. */
  caption?: string;
  loading?: boolean;
  /** Smaller rows for long registers, whatever the person chose. */
  dense?: boolean;
  /**
   * Names the table for its "View" button: the person can then hide columns, keep a sort, and save named views
   * of it (kept per person on this device). The name must stay the same over time, e.g. "invoices.list".
   */
  view?: string;
}) {
  const [saved, setSaved] = usePref<TableView>(`table.${view ?? '-'}`, {
    hidden: [],
    sort: null,
    views: [],
  });
  const [density, setDensity] = usePref<Density>('table.density', 'comfortable');
  const [local, setLocal] = useState<Sort>(null);
  // A sort that names a column the table no longer has (fewer columns for this role) is ignored.
  const fit = (x: Sort) => (x && x.col < head.length && head[x.col] ? x : null);
  const by = view ? fit(saved.sort) : local;
  const setBy = (f: (b: Sort) => Sort) => (view ? setSaved({ ...saved, sort: f(by) }) : setLocal(f));
  // Columns the person chose to hide; the first one (what the row is) always stays.
  const hidden = view ? saved.hidden.filter((i) => i > 0 && i < head.length && head[i]) : [];
  const cols = head.map((_, i) => i).filter((i) => !hidden.includes(i));
  const cls = (i: number) => [phoneHide.includes(i) ? 'hide-phone' : '', left.includes(i) ? 'left' : ''].filter(Boolean).join(' ') || undefined;
  const canSort = sortable && rows.length > 1;

  const shown = useMemo(() => {
    if (!by || !canSort) return rows;
    const loc = sortLocale(locale());
    const collator = new Intl.Collator(locale(), {
      numeric: true,
      sensitivity: 'base',
    });
    const keyed = rows.map((r, at) => ({
      r,
      at,
      k: r.sort?.[by.col] !== undefined ? r.sort[by.col]! : sortValue(nodeText(r.cells[by.col]), loc),
    }));
    keyed.sort((a, b) => {
      // Empty cells stay at the bottom in both directions; equal rows keep their order.
      if (a.k === null || b.k === null) return a.k === b.k ? a.at - b.at : a.k === null ? 1 : -1;
      const c = compareKeys(a.k, b.k, collator);
      return (by.dir === 'asc' ? c : -c) || a.at - b.at;
    });
    return keyed.map((x) => x.r);
  }, [rows, by, canSort]);

  const press = (col: number) =>
    setBy((b) =>
      b?.col !== col
        ? { col, dir: col === 0 || left.includes(col) ? 'asc' : 'desc' }
        : b.dir === (col === 0 || left.includes(col) ? 'asc' : 'desc')
          ? { col, dir: b.dir === 'asc' ? 'desc' : 'asc' }
          : null,
    );

  // The cell that carries the row's button (for the keyboard and screen readers): the first titled
  // cell with nothing else to press in it, so a button never wraps a link, a checkbox or an icon.
  const linkAt = (r: DataRow) => (r.onClick ? r.cells.findIndex((c, i) => !!head[i] && !hidden.includes(i) && !interactive(c)) : -1);

  if (loading) return <Skeleton h={240} r="var(--radius-lg)" />;

  const compact = dense || density === 'compact';
  const table = (
    <table className={`data-table${compact ? ' dense' : ''}`}>
      {caption ? <caption className="sr-only">{caption}</caption> : null}
      <thead>
        <tr>
          {cols.map((i) => {
            const h = head[i]!;
            const on = by?.col === i;
            const sort = on ? (by!.dir === 'asc' ? 'ascending' : 'descending') : undefined;
            return (
              <th key={h || i} scope="col" className={cls(i)} aria-sort={canSort && h ? (sort ?? 'none') : undefined}>
                {canSort && h ? (
                  <button type="button" className={`th-sort${on ? ' on' : ''}`} onClick={() => press(i)} title={t('dt.sort_by', { name: h })}>
                    <span>{h}</span>
                    <Icon name={on ? (by!.dir === 'asc' ? 'arrowUp' : 'arrowDown') : 'sort'} size={14} />
                  </button>
                ) : (
                  h
                )}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {shown.length === 0 ? (
          <tr>
            <td colSpan={cols.length} className="muted left data-table-empty">
              {empty}
            </td>
          </tr>
        ) : (
          shown.map((r) => (
            <tr key={r.key} className={[r.onClick ? 'clickable' : '', r.selected ? 'selected' : ''].filter(Boolean).join(' ') || undefined} onClick={r.onClick}>
              {cols
                .map((i) => [i, r.cells[i]] as const)
                .map(([i, c]) => (
                  <td key={i} className={cls(i)}>
                    {i === linkAt(r) ? (
                      <button type="button" className="row-link" onClick={(e) => (e.stopPropagation(), r.onClick!())}>
                        {c}
                      </button>
                    ) : (
                      c
                    )}
                  </td>
                ))}
            </tr>
          ))
        )}
      </tbody>
      {foot ? (
        <tfoot>
          <tr>
            {cols.map((i) => (
              <td key={i} className={cls(i)}>
                {foot[i]}
              </td>
            ))}
          </tr>
        </tfoot>
      ) : null}
    </table>
  );
  if (!view) return table;
  return (
    <>
      <div className="dt-tools">
        <TableViewPanel head={head} view={saved} onView={setSaved} density={density} onDensity={setDensity} sortable={sortable} />
      </div>
      {table}
    </>
  );
}
